import { useEffect, useState } from 'react'
import { getPortalHearings } from '@services/portalService'
import { ReportEmptyState, formatReportDate, formatReportNumber } from '@components/shared/reports/ReportPrimitives'
import PortalRegionFilters from './PortalRegionFilters'

function dateInputValue(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function defaultFilters() {
  const today = new Date()
  const end = new Date(today)
  end.setDate(end.getDate() + 90)
  return { dataInicio: dateInputValue(today), dataFim: dateInputValue(end), estado: '', comarca: '', busca: '' }
}

function formatConsultedAt(value) {
  if (!value) return 'Não informada'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Não informada' : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

export default function PortalHearingsPanel({ onProcessSelect }) {
  const [filters, setFilters] = useState(defaultFilters)
  const [draft, setDraft] = useState(filters)
  const [page, setPage] = useState(1)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterError, setFilterError] = useState('')
  const [refreshVersion, setRefreshVersion] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    getPortalHearings(filters, { page }).then((response) => {
      if (!active) return
      if (response.success) {
        const lastPage = Math.max(1, Math.ceil((response.data?.total || 0) / (response.data?.tamanhoPagina || 25)))
        if (page > lastPage) {
          setPage(lastPage)
          return
        }
        setResult(response.data)
      } else setError(response.error || 'Não foi possível carregar a pauta de audiências.')
      setLoading(false)
    })
    return () => { active = false }
  }, [filters, page, refreshVersion])

  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value, ...(field === 'estado' ? { comarca: '' } : {}) }))
    setFilterError('')
  }
  const apply = (event) => {
    event.preventDefault()
    const days = (Date.parse(draft.dataFim) - Date.parse(draft.dataInicio)) / 86400000
    if (!draft.dataInicio || !draft.dataFim || !Number.isFinite(days) || days < 0 || days > 365) {
      setFilterError('Informe um período válido de até 366 dias, com a data final igual ou posterior à inicial.')
      return
    }
    setFilterError('')
    setFilters(Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, value.trim()])))
    setPage(1)
  }
  const clear = () => {
    const next = defaultFilters()
    setDraft(next)
    setFilters(next)
    setFilterError('')
    setPage(1)
  }
  const reload = () => setRefreshVersion((value) => value + 1)
  const totalPages = Math.max(1, Math.ceil((result?.total || 0) / (result?.tamanhoPagina || 25)))
  const filtersChanged = Object.keys(filters).some((key) => draft[key].trim() !== filters[key])
  const filterDescription = [filters.estado, filters.comarca, filters.busca ? `Busca: ${filters.busca}` : ''].filter(Boolean).join(' · ')

  return (
    <>
      <section className="portal-hero">
        <div><span className="portal-eyebrow">Agenda processual</span><h1>Pauta de audiências</h1><p>Audiências cadastradas nos processos da sua empresa.</p></div>
        <button type="button" className="portal-secondary-button" onClick={reload} disabled={loading}><i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} /> Recarregar pauta</button>
      </section>
      <section className="portal-section portal-filter-section" aria-label="Filtros das audiências">
        <form className="portal-filter-bar portal-hearing-filters" onSubmit={apply}>
          <label className="portal-field"><span>De</span><input type="date" value={draft.dataInicio} max={draft.dataFim || undefined} onChange={(event) => update('dataInicio', event.target.value)} required /></label>
          <label className="portal-field"><span>Até</span><input type="date" value={draft.dataFim} min={draft.dataInicio || undefined} onChange={(event) => update('dataFim', event.target.value)} required /></label>
          <PortalRegionFilters estado={draft.estado} comarca={draft.comarca} onChange={update} refreshVersion={refreshVersion} />
          <label className="portal-field portal-filter-search"><span>Processo ou parte</span><input value={draft.busca} onChange={(event) => update('busca', event.target.value)} placeholder="Número do processo ou parte" maxLength={200} /></label>
          <div className="portal-filter-actions"><button type="submit" className="portal-primary-button" disabled={loading}>Aplicar filtros</button><button type="button" className="portal-secondary-button" onClick={clear} disabled={loading}>Limpar</button></div>
        </form>
        {filterError ? <div className="portal-form-error" role="alert">{filterError}</div> : null}
        <p className="portal-filter-note">{filtersChanged ? 'Aplique os filtros para atualizar a pauta.' : `Período: ${formatReportDate(filters.dataInicio)} a ${formatReportDate(filters.dataFim)}${filterDescription ? ` · ${filterDescription}` : ''}.`}</p>
      </section>
      <section className="portal-section portal-hearings-section" aria-label="Audiências encontradas">
        <div className="portal-section-heading"><div><span className="portal-eyebrow">{formatReportDate(filters.dataInicio)} a {formatReportDate(filters.dataFim)}</span><h2>Audiências dos seus processos</h2></div>{!loading && !error && result ? <span className="portal-result-count">{formatReportNumber(result.total)} audiências encontradas</span> : null}</div>
        {loading ? <div className="portal-inline-loading" role="status"><i className="fas fa-spinner fa-spin" /> Carregando pauta de audiências...</div> : error ? (
          <div className="portal-form-error portal-retry-error" role="alert"><span>{error}</span><button type="button" className="portal-secondary-button" onClick={reload}>Tentar novamente</button></div>
        ) : result?.itens?.length ? (
          <div className="portal-table-wrap"><table className="portal-process-table portal-hearings-table"><thead><tr><th>Data e hora</th><th>Processo</th><th>Audiência</th><th>Estado / comarca</th><th>Foro / vara</th><th>Status</th></tr></thead><tbody>
            {result.itens.map((item) => (
              <tr key={item.id}>
                <td><strong>{formatReportDate(item.data)}</strong><small>{item.hora ? String(item.hora).slice(0, 5) : 'Horário não informado'}</small></td>
                <td><button type="button" className="portal-process-detail-link" onClick={() => onProcessSelect(item.processoId)} aria-label={`Ver detalhes do processo ${item.numeroProcesso || item.processoId}`}><strong>{item.numeroProcesso || `#${item.processoId}`}</strong></button><small>{item.parteContraria || 'Parte contrária não informada'}</small></td>
                <td><strong>{item.tipo || 'Audiência'}</strong></td>
                <td><strong>{item.estado || 'Estado não informado'}</strong><small>{item.comarca || 'Comarca não informada'}</small></td>
                <td><strong>{item.foro || 'Foro não informado'}</strong><small>{item.vara || 'Vara não informada'}</small></td>
                <td><span className="portal-status">{item.status || 'Não informado'}</span></td>
              </tr>
            ))}
          </tbody></table></div>
        ) : <ReportEmptyState title="Nenhuma audiência encontrada" description="Não há audiências cadastradas para os filtros e o período selecionados." />}
        {!loading && !error && totalPages > 1 ? <div className="portal-pagination"><span>Página {result.pagina} de {totalPages}</span><div><button type="button" disabled={result.pagina <= 1} onClick={() => setPage(result.pagina - 1)}>Anterior</button><button type="button" disabled={result.pagina >= totalPages} onClick={() => setPage(result.pagina + 1)}>Próxima</button></div></div> : null}
        {!loading && !error && result ? <p className="portal-data-updated"><i className="far fa-clock" /> Pauta consultada em {formatConsultedAt(result.consultadoEm)}. Horários conforme o cadastro da audiência.</p> : null}
      </section>
    </>
  )
}
